import { EditorConfig } from "ckeditor5";
import React, { PropsWithChildren, createContext, useContext } from "react";

import { useStabilized } from "@examspark/common-ui";

import { AnalyticsEvent } from "../plugins/Analytics";
import { SharedResourceApi } from "../plugins/SharedResourceInsert";
import { TableStyleDefinition } from "../plugins/TableStyles";
import { FileUploadApi } from "../plugins/UploadWithContext";

export type RichContentConfig = {
  enableInspector?: boolean;
  licenseKey?: string;

  sharedResourceApi?: SharedResourceApi;
  fileUploadApi?: FileUploadApi;

  onAnalyticsEvent?: (event: AnalyticsEvent) => void;
  onConfig?: (config: EditorConfig) => void;

  tableStyles?: TableStyleDefinition[];

  override?: boolean;
};

const RichContentConfigContext = createContext<RichContentConfig | undefined>(
  undefined,
);

export const useRichContentConfig = () => useContext(RichContentConfigContext);

export const RichContentConfigProvider = ({
  children,
  override,
  ...props
}: PropsWithChildren<RichContentConfig>) => {
  const parentContext = useContext(RichContentConfigContext);

  const propsStabilized = useStabilized(props, [
    //
    // Avoid rerenders for things that might be passed in volatilely
    //
    props.enableInspector,
    props.licenseKey,
    !!props.fileUploadApi,
    !!props.sharedResourceApi,
    !!props.tableStyles,
    !!props.onAnalyticsEvent,
    !!props.onConfig,
  ]);

  if (parentContext != null && !override) {
    console.error(
      `rce_config`,
      "Refusing to override RichContentConfig provided at a higher level. To force, pass the `override` prop.",
    );
    return <>{children}</>;
  }

  return (
    <RichContentConfigContext.Provider value={propsStabilized}>
      {children}
    </RichContentConfigContext.Provider>
  );
};
